import { POPPINS_UNITS, POPPINS_WIDTHS } from './poppinsMetrics.generated';

/**
 * **The line limits, published once** (CONTENT_ENGINE.md §3.2, adopted 23 September): every string a child reads has
 * a role, and each role has a type run, a measure (the text column at 360pt, the narrowest supported width) and a
 * line limit. Text is never shrunk and never truncated — too much text is an authoring error. The portal's live
 * counters (content engine part 2) and the publish gate (part 3) read these numbers, so they cannot disagree.
 *
 * Measurement needs glyph widths, which this package cannot have: the caller passes a `TextWidth`. The portal
 * measures with a canvas in Poppins, the way the prototype in `docs/handoff/design/content-engine/engine.js` does.
 */

/** A type run: the weight and size a role is set in. */
export interface QuestTypeRun {
  /** The app's name for the run, as the board prints it: "prompt 20/700". */
  run: string;
  weight: 500 | 700 | 800;
  size: number;
}

export interface QuestLineRole extends QuestTypeRun {
  /** The text column at 360pt, in points. */
  measure: number;
  /** How many lines the role holds. */
  lines: number;
}

export type QuestLineRoleKey =
  | 'beat'
  | 'choice'
  | 'consequence'
  | 'question'
  | 'tileCaption'
  | 'tileType'
  | 'feedback';

/** §3.2's table. `tileType`'s run is set by the row (`rowTileStep`), so its own is the widest it can take. */
export const QUEST_TEXT_ROLES: Readonly<Record<QuestLineRoleKey, QuestLineRole>> = {
  beat: { run: 'prompt 20/700', weight: 700, size: 20, measure: 263, lines: 6 },
  choice: { run: 'choice 16/700', weight: 700, size: 16, measure: 221, lines: 2 },
  consequence: { run: 'rowTitleBold 16/800', weight: 800, size: 16, measure: 131, lines: 6 },
  question: { run: 'title 28/800', weight: 800, size: 28, measure: 312, lines: 4 },
  tileCaption: { run: 'caption 17/800', weight: 800, size: 17, measure: 113, lines: 2 },
  tileType: { run: 'caption 17/800', weight: 800, size: 17, measure: 113, lines: 3 },
  feedback: { run: 'explain 16/500', weight: 500, size: 16, measure: 131, lines: 6 },
};

/** The four steps a type tile can take (§3.3; apps/mobile questArt.tileTypeVariant). */
export const QUEST_TILE_STEPS = {
  title: { run: 'title 28/800', weight: 800, size: 28 },
  prompt: { run: 'prompt 20/700', weight: 700, size: 20 },
  caption: { run: 'caption 17/800', weight: 800, size: 17 },
  pillSm: { run: 'pillSm 14/800', weight: 800, size: 14 },
} as const satisfies Record<string, QuestTypeRun>;

export type QuestTileStep = keyof typeof QUEST_TILE_STEPS;

/** One tile's step from its own length — the app's rule, applied per row by `rowTileStep`. */
function tileStep(caption: string): QuestTileStep {
  const n = caption.trim().length;
  return n <= 4 ? 'title' : n <= 8 ? 'prompt' : n <= 16 ? 'caption' : 'pillSm';
}

/** §3.3 "One size per row": the row's type size is set by its longest caption. */
export function rowTileStep(captions: readonly string[]): QuestTileStep {
  const longest = captions.reduce((a, b) => (b.trim().length > a.trim().length ? b : a), '');
  return tileStep(longest);
}

/** The role a type tile is measured as, in its row's step. */
export function tileTypeRole(captions: readonly string[]): QuestLineRole {
  const step = QUEST_TILE_STEPS[rowTileStep(captions)];
  return { ...QUEST_TEXT_ROLES.tileType, run: step.run, weight: step.weight, size: step.size };
}

/** The width of `text` set in `run`, in points. */
export type TextWidth = (text: string, run: QuestTypeRun) => number;

/**
 * **The ruler both sides use** — the portal's counters and the publish gate: Poppins's advance widths, measured once by
 * the browser canvas the content-engine checker is built on (`tools/poppins-metrics`). That canvas applies no pair
 * kerning, so a string's width is the sum of its characters'. A character the table does not hold is measured as the
 * widest of its weight, so an unknown one can only make a line longer — the gate errs toward "too long".
 */
export const poppinsWidth: TextWidth = (text, run) => {
  const table = POPPINS_WIDTHS[run.weight];
  let units = 0;
  for (const ch of text) units += table[ch] ?? WIDEST[run.weight];
  return (units * run.size) / POPPINS_UNITS;
};

const WIDEST: Record<500 | 700 | 800, number> = {
  500: Math.max(...Object.values(POPPINS_WIDTHS[500])),
  700: Math.max(...Object.values(POPPINS_WIDTHS[700])),
  800: Math.max(...Object.values(POPPINS_WIDTHS[800])),
};

/**
 * **Greedy word wrap at the role's measure** — exactly one line per hand-typed `\n`, bold markers taking no width,
 * and a word wider than the column broken where it has to be. The rows a child's phone draws, as the prototype
 * measures them. Empty text is no lines.
 */
export function wrapLines(text: string, role: QuestLineRole, width: TextWidth): string[] {
  const clean = text.replace(/\*\*/g, '');
  if (!clean.trim()) return [];
  const fits = (t: string) => width(t, role) <= role.measure;
  const rows: string[] = [];
  for (const para of clean.split('\n')) {
    let cur = '';
    for (const word of para.split(/\s+/).filter(Boolean)) {
      const t = cur ? `${cur} ${word}` : word;
      if (fits(t)) {
        cur = t;
        continue;
      }
      if (cur) rows.push(cur);
      let rest = word;
      while (rest.length > 1 && !fits(rest)) {
        let i = rest.length - 1;
        while (i > 1 && !fits(rest.slice(0, i))) i--;
        rows.push(rest.slice(0, i));
        rest = rest.slice(i);
      }
      cur = rest;
    }
    rows.push(cur);
  }
  return rows;
}

/** How many lines `text` takes in `role`, and whether that is over its limit. */
export function lineCount(
  text: string,
  role: QuestLineRole,
  width: TextWidth,
): { lines: number; limit: number; over: boolean } {
  const lines = wrapLines(text, role, width).length;
  return { lines, limit: role.lines, over: lines > role.lines };
}
